import classNames from 'classnames'
import * as React from 'react'

import { Button, Delayed, Icon, Spinner, useTour } from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import { getRequestFullscreen, isRedesignEnabled } from '../../utils'

interface TourDefinition {
  id: string
  steps: Array<{
    title?: React.ReactNode
    body: React.ReactNode
    target?: string
    placement?: string
    overlayRadius?: number
    overlayPadding?: number
  }>
  skip?: boolean
}

// If we need to update / discontinue some tour we can use skip flag on legacy version to ensure that it won't reset users tour but won't include it in new tours
const getTour = (page: SpecificChartFunctionality): TourDefinition[] => [
  {
    id: 'chart-tour',
    steps: [
      {
        title: 'Chart Tour',
        body: 'Discover powerful Chart features in the 2-min tour to master your analysis of trading opportunities.',
      },
      {
        title: 'Scale the Timeline',
        body: 'Grab the timeline to zoom it in and out. Double-click the timeline to reset the scaling.',
        target: '[data-testid="chart-0-pane-0-zoomer"]',
      },
      {
        title: 'Scale the Price Axis',
        body: 'Grab the Price Axis to zoom it in and out. Move the chart around freely in any direction when Price Axis has custom scale. Double-click the Price Axis to reset the scaling.',
        target: '[data-testid="chart-0-pane-0-scaler"]',
      },
      {
        title: 'Drawing tools & settings',
        body: 'Pick any Drawing Tool to analyze the market. Tailor your drawing experience with additional settings.',
        target: '#toolbar',
      },
      {
        title: 'Fine-tune Drawings',
        body: 'Double-click on a chart drawing to tweak its color and style. Right-click on a drawing to access other options, such as changing its visibility, order, and more.',
        target: '[data-testid="chart-0-pane-0"]',
      },
      {
        title: 'Save your best Ideas',
        body: 'Besides your drawings being autosaved, you can manually save ideas that you wish to revisit later.',
        target: '[data-testid="chart-toolbar-ideas"]',
      },
      ...(page === SpecificChartFunctionality.quotePage
        ? []
        : [
            {
              title: 'Chart Layouts & Charts Sync',
              body: 'Switch between Chart Layouts to view multiple charts on your screen. Synchronize Ticker, Timeframe or Crosshair position across your charts.',
              target: '[data-testid="chart-layout-layout"]',
            },
          ]),
      ...(!getRequestFullscreen(document.documentElement) && page === SpecificChartFunctionality.chartPage
        ? []
        : [
            {
              title: 'Fullscreen mode',
              body:
                page === SpecificChartFunctionality.quotePage
                  ? 'Open the chart in an expanded view for full-page display and split-screen charts.'
                  : 'Toggle the window to fill your entire screen.',
              target: '[data-testid="chart-layout-fullscreen"]',
            },
          ]),
      {
        title: 'Publish chart',
        body: 'Publish a chart together with your analysis and ideas.',
        target: '[data-testid="chart-toolbar-publish"]',
        overlayPadding: page === SpecificChartFunctionality.quotePage ? 3 : undefined,
      },
      ...(isRedesignEnabled() || page === SpecificChartFunctionality.chartPage
        ? [
            {
              title: 'Theme',
              body: 'Switch between the Light and Dark theme.',
              target: '[data-testid="chart-layout-theme"]',
            },
          ]
        : []),
      {
        title: 'Study Settings',
        body: 'Customize the Chart Type and appearance. Add Overlays, Indicators and toggle Extended Hours.',
        target: '[data-testid="chart-settings-open-btn"]',
      },
    ],
  },
]

export function TourButton({
  page,
  rounding,
  theme,
  dropdownButtonClassName,
}: {
  page: SpecificChartFunctionality
  rounding?: 'none' | 'regular'
  theme?: string
  dropdownButtonClassName?: string
}) {
  const tour = useTour()
  const pageTours = getTour(page)
  const [userTour, setCurrentTour] = React.useState(tour.getStepsForUser(pageTours))

  return (
    <Button
      theme={theme ?? (userTour.hasNewTours ? 'blue' : 'headerItem')}
      rounding={rounding}
      leftContent={
        tour.isLoading ? (
          <Delayed delayComponent={<Icon name="diamondOutlined" className="text-white" />}>
            <Spinner width={16} />
          </Delayed>
        ) : (
          <Icon name="diamondOutlined" />
        )
      }
      className={classNames(dropdownButtonClassName, {
        'mobile:hidden': !!dropdownButtonClassName,
        'is-primary !mr-[5px]': userTour.hasNewTours && page === SpecificChartFunctionality.quotePage,
      })}
      contentClass={page !== SpecificChartFunctionality.quotePage ? 'hidden lg:flex' : ''}
      title="Explore new features"
      onClick={async () => {
        if (userTour) {
          const activeTour = await tour.startTour(userTour.tour)
          activeTour?.once('inactive', () => {
            setCurrentTour(tour.getStepsForUser(pageTours))
          })
        }
      }}
    >
      Chart tour
    </Button>
  )
}
